import { scopedChannelApi } from '@/session/scopedClients';
import type { RequestScope } from '@/session/scopedTransport';
import type { ChannelApiResponse } from '@/types/endpoints';
import { utils } from '@/utils';
import type { ReadRequestOptions } from '../../requestOptions';
import type { ExpansionFamilyRow, ExpansionFamilyStatus } from './types';

const MAX_PER_PAGE = 200;

const validate = (runId: number, subscriptionId: number, page?: number, perPage?: number) => {
  if (!runId || runId < 1) {
    throw new Error('Valid run ID is required');
  }
  if (!subscriptionId || subscriptionId < 1) {
    throw new Error('Valid subscription ID is required');
  }
  if (page !== undefined && (!page || page < 1)) {
    throw new Error('page must be a positive number');
  }
  if (perPage !== undefined && (!perPage || perPage < 1 || perPage > MAX_PER_PAGE)) {
    throw new Error(`perPage must be between 1 and ${MAX_PER_PAGE}`);
  }
};

type RequestParams = {
  runId: number;
  /**
   * Any subscription of the organization — the API authorises the caller against it and scopes
   * every read by organization, never by this id.
   */
  subscriptionId: number;
  page?: number;
  perPage?: number;
  status?: ExpansionFamilyStatus;
  /** A commonality key: families with at least one variant still blocked on it. */
  key?: string;
  q?: string;
};

export type GetRunFamiliesResponse = {
  data: ExpansionFamilyRow[];
  total: number;
};

export const getRunFamilies = async (
  scope: RequestScope,
  { runId, subscriptionId, page, perPage, status, key, q }: RequestParams,
  options: ReadRequestOptions
): Promise<ChannelApiResponse<GetRunFamiliesResponse>> => {
  try {
    validate(runId, subscriptionId, page, perPage);

    const result = await scopedChannelApi(scope).get<GetRunFamiliesResponse>(`atts/expansion/runs/${runId}/families`, {
      ...options,
      headers: { 'x-astur-subscription-id': subscriptionId.toString() },
      params: { page, per_page: perPage, status, key, q },
    });

    return { data: result.data, status: result.status };
  } catch (e) {
    return utils.error.handleAxiosError(e);
  }
};
